<template>
  <div class="dashboard-view">
    <BaseContainer
      :active-spacer="true"
      base-container-classes="pb-0"
    >
      <template v-slot:header-title>
        <template v-if="currentScreenData">
          {{ currentScreenData.title }}
        </template>
      </template>
      <template v-slot:header-title-description>
        <template v-if="currentScreenData">
          {{ currentScreenData && currentScreenData.description }}
        </template>
      </template>
      <template v-slot:header-actions>
        <v-menu
          v-if="dynamicNavigationFetched && params.length"
          v-model="filtersMenuOpen"
          :close-on-content-click="false"
          offset-y
          left
        >
          <template v-slot:activator="{ on, attrs }">
            <v-badge
              :value="!allFilterValuesFilled"
              color="warning"
              dot
              overlap
            >
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-filter-variant</v-icon>
              </v-btn>
            </v-badge>
          </template>
          <v-card class="pa-4 dashboard-view__filters-card">
            <div
              class="app-align-items-center"
              style="flex-wrap: wrap;"
            >
              <template v-for="param in params">
                <BaseDatePicker
                  v-if="param.type === 'date'"
                  :key="param.name"
                  :name-placeholder="paramLabel(param)"
                  :date-value="filterValues[param.name]"
                  :max-width="118"
                  locale="pt-br"
                  time-zone="UTC"
                  class="dashboard-view__filter-field"
                  @on-input-date-picker="(value) => { filterValues[param.name] = value }"
                />
                <BaseInput
                  v-else
                  :key="param.name"
                  v-model="filterValues[param.name]"
                  :label="paramLabel(param)"
                  :type="param.type === 'number' ? 'number' : 'text'"
                  class="my-4 ml-4 dashboard-view__filter-field"
                />
              </template>
              <BaseButton
                type="text"
                color="primary"
                :is-disabled="!allFilterValuesFilled"
                @click="fetchScreen({ resetScreen: true }); filtersMenuOpen = false"
              >
                Buscar
              </BaseButton>
            </div>
          </v-card>
        </v-menu>
      </template>
      <DashboardScreen
        v-if="currentScreenData"
        :key="screenRenderKey"
        class="my-2"
        :screen="currentScreen"
        hide-header
        @row-detail="onRowDetail"
        @input="onDrawerInput"
        @filter-change="onFilterChange"
        @update:options="onTableOptionsChange"
        @update:reference-date="onReferenceDateChange"
        @submit="onFilterSubmit"
        @action="onAction"
      />
      <BaseLoadingState
        v-else-if="isFetchingScreen || !dynamicNavigationFetched"
        class="my-2 dashboard-view__loading"
      />
      <div
        v-else-if="showPendingCard"
        class="d-flex justify-center dashboard-view__pending-filters"
        style="height: calc(100vh - 180px)"
      >
        <div class="text-center align-self-center px-6">
          <v-icon
            size="40"
            color="grey"
          >
            mdi-filter-variant
          </v-icon>
          <template v-if="!allFilterValuesFilled">
            <p class="mt-3 mb-4">
              Esta tela tem filtros obrigatórios pendentes. Preencha-os para continuar.
            </p>
            <BaseButton
              color="primary"
              @click="filtersMenuOpen = true"
            >
              Preencher filtros
            </BaseButton>
          </template>
          <template v-else>
            <p class="mt-3 mb-4">
              Os filtros já estão preenchidos. Clique em "Buscar" para carregar esta tela.
            </p>
            <BaseButton
              color="primary"
              @click="fetchScreen()"
            >
              Buscar
            </BaseButton>
          </template>
        </div>
      </div>
      <BaseDetailDrawer
        v-for="entry in openIndependentDrawersList"
        :key="entry.id"
        :value="true"
        :title="entry.row.detail.props.title || ''"
        :subtitle="entry.row.detail.props.subtitle || ''"
        :width="entry.row.detail.props.width"
        @input="(value) => onIndependentDrawerInput(entry.id, value)"
      >
        <DashboardLazyRef
          v-if="entry.row.detail.type === 'lazy_ref'"
          :instance="entry.row.detail.props.instance"
          :params="entry.row.detail.props.params"
          @row-detail="onRowDetail"
          @filter-change="onFilterChange"
          @update:options="onTableOptionsChange"
          @submit="onFilterSubmit"
          @action="onAction"
        />
        <template v-else>
          <DashboardNodeRenderer
            v-for="(child, index) in (entry.row.detail.children || [])"
            :key="child.id || index"
            :node="child"
            :data-field="independentDrawerChildDataField(entry.row.detail, child, index)"
            @row-detail="onRowDetail"
            @filter-change="onFilterChange"
            @update:options="onTableOptionsChange"
            @submit="onFilterSubmit"
            @action="onAction"
          />
        </template>
      </BaseDetailDrawer>
    </BaseContainer>
    <BaseSnackbar
      :active-snackbar="feedback"
      :type-snackbar="feedbackColor"
      :text-snackbar="feedbackMessage"
      @closeSnackbar="feedback = !feedback"
    />
  </div>
</template>

<script>
import DashboardScreen from '../../Dashboard/DashboardScreen.vue'
import DashboardNodeRenderer from '../../Dashboard/DashboardNodeRenderer.vue'
import DashboardLazyRef from '../../Dashboard/DashboardLazyRef.vue'
import BaseDetailDrawer from '../../Dashboard/BaseDetailDrawer.vue'
import BaseDatePicker from '../../BaseDatePicker'
import BaseLoadingState from '../../BaseLoadingState.vue'
import { mapActions, mapState, mapGetters } from "vuex"
import BaseSnackbarConfiguration from '../../../mixins/BaseSnackbarConfiguration'
import DashboardIndependentDrawers, { TABLE_FILTER_QUERY_PREFIX, RESERVED_QUERY_KEYS } from '../../../mixins/DashboardIndependentDrawers'
import { withoutEmptyValues } from '../../../mixins/DashboardTableQueryState'
import DashboardActionDispatch from '../../../mixins/DashboardActionDispatch'
import BaseSnackbar from '../../BaseSnackbar'

export default {
  name: 'DashboardView',
  components: { DashboardScreen, DashboardNodeRenderer, DashboardLazyRef, BaseDetailDrawer, BaseDatePicker, BaseLoadingState, BaseSnackbar },
  mixins: [BaseSnackbarConfiguration, DashboardIndependentDrawers, DashboardActionDispatch],
  data() {
    return {
      filterValues: {},
      extraQueryParams: {},
      appliedQueryKey: null,
      currentScreenData: null,
      filtersMenuOpen: false,
      isFetchingScreen: false
    }
  },
  computed: {
    ...mapState({
      programInitiator: state => state.platformProgram.programInitiator,
      dynamicNavigationFetched: state => state.dynamicNavigation.fetched
    }),
    ...mapGetters(['paramsForView']),
    baseScreen() {
      return this.currentScreenData
    },
    params() {
      return this.paramsForView(this.$route.params.view).filter((param) => param.name !== 'program_id')
    },
    requiredParams() {
      return this.params.filter((param) => param.required !== false)
    },
    allFilterValuesFilled() {
      return this.requiredParams.every((param) => {
        const value = this.filterValues[param.name]
        return value !== null && value !== undefined && value !== ''
      })
    },
    showPendingCard() {
      return this.dynamicNavigationFetched && !this.isFetchingScreen && !this.currentScreenData
    }
  },
  watch: {
    '$route.params.view'() {
      this.resetScreenState()
      if (!this.dynamicNavigationFetched) {
        return
      }
      this.initFilterValues()
      this.maybeAutoFetch()
    },
    '$route.query'(query) {
      if (JSON.stringify(query || {}) === this.appliedQueryKey) {
        return
      }
      this.resetScreenState()
      if (!this.dynamicNavigationFetched) {
        return
      }
      this.initFilterValues()
      this.maybeAutoFetch()
    },
    dynamicNavigationFetched(value) {
      if (value) {
        this.initFilterValues()
        this.maybeAutoFetch()
      }
    }
  },
  created() {
    if (this.dynamicNavigationFetched) {
      this.initFilterValues()
      this.maybeAutoFetch()
    }
  },
  methods: {
    ...mapActions(["apiGetScreen", "apiGetProgramInfo"]),
    paramLabel(param) {
      const label = param.label || param.name
      return param.required !== false ? `${label} *` : label
    },
    resetScreenState() {
      this.currentScreenData = null
      this.filtersMenuOpen = false
      this.openRow = null
      this.openIndependentDrawers = {}
      this.tableFilterValues = {}
      this._declaredTableFilterParams = {}
      this.tableSearchValues = {}
      this.tablePages = {}
      this.tableItemsPerPage = {}
      this._lastTableOptions = {}
      this._tableOptionsSnapshots = {}
      this.referenceDateValues = {}
      this.screenRenderKey = 0
      this.screenRenderTargets = []
    },
    initFilterValues() {
      const query = this.$route.query || {}
      const values = {}
      this.params.forEach((param) => {
        values[param.name] = param.type === 'date' ? new Date().toISOString().slice(0, 10) : ''
      })
      const extra = {}
      Object.keys(query).forEach((key) => {
        if (key.startsWith(TABLE_FILTER_QUERY_PREFIX)) {
          const [instance, ...paramParts] = key.slice(TABLE_FILTER_QUERY_PREFIX.length).split('.')
          const param = paramParts.join('.')
          if (instance && param) {
            this.$set(this.tableFilterValues, instance, { ...(this.tableFilterValues[instance] || {}), [param]: query[key] })
          }
        } else if (Object.prototype.hasOwnProperty.call(values, key)) {
          values[key] = query[key]
        } else if (!RESERVED_QUERY_KEYS.includes(key)) {
          extra[key] = query[key]
        }
      })
      this.filterValues = values
      this.extraQueryParams = extra
      this.appliedQueryKey = JSON.stringify(query)
    },
    maybeAutoFetch() {
      if (this.allFilterValuesFilled) {
        this.fetchScreen()
      } else {
        this.filtersMenuOpen = true
      }
    },
    async fetchScreen({ resetScreen = false } = {}) {
      const requestedView = this.$route.params.view
      const tableQueryRequestId = this.startTableQueryRequest()
      this.isFetchingScreen = true
      if (resetScreen) {
        this.currentScreenData = null
      }
      try {
        if (!this.programInitiator) {
          await this.apiGetProgramInfo()
        }
        const result = await this.apiGetScreen({
          view: requestedView,
          programId: this.programInitiator,
          params: { ...withoutEmptyValues({ ...this.extraQueryParams, ...this.filterValues }), ...this.tableQueryParams }
        })
        if (this.$route.params.view !== requestedView || !this.isLatestTableQueryRequest(tableQueryRequestId)) {
          return
        }
        this.currentScreenData = result
        this.confirmTableOptions(tableQueryRequestId)
        this.resyncOpenDrawers()
      } catch (error) {
        if (this.$route.params.view !== requestedView || !this.isLatestTableQueryRequest(tableQueryRequestId)) {
          return
        }
        this.rollbackTableOptions(tableQueryRequestId)
        const missingParams = error.response && error.response.status === 400 && error.response.data && error.response.data.missingParams
        this.showFeedback({
          color: "error",
          message: Array.isArray(missingParams) && missingParams.length
            ? `Parâmetros obrigatórios faltando: ${missingParams.join(', ')}.`
            : 'Erro ao buscar tela.',
        })
      } finally {
        if (this.$route.params.view === requestedView && this.isLatestTableQueryRequest(tableQueryRequestId)) {
          this.isFetchingScreen = false
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard-view {
  &__filters-card {
    min-width: 320px;
    max-width: 90vw;
  }

  &__filter-field {
    min-width: 160px;
  }

  &__pending-filters {
    color: var(--ds-color-black-055);
  }

  &__loading {
    color: var(--ds-color-black-055);
  }
}
</style>
